
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * SwapImportSymbolSettings struct
 */
class SwapImportSymbolSettings {
  /**
   * Initialize struct
   * @param {!string=} symbolMask
   * @param {!boolean=} isSwapEnabledSymbol
   * @constructor
   */
  constructor (symbolMask = '', isSwapEnabledSymbol = false) {
    this.SymbolMask = symbolMask
    this.IsSwapEnabledSymbol = isSwapEnabledSymbol
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapImportSymbolSettings}
   * @param {!SwapImportSymbolSettings} other Other struct
   * @returns {!SwapImportSymbolSettings} This struct
   */
  copy (other) {
    if (other.SymbolMask != null) {
      this.SymbolMask = other.SymbolMask
    } else {
      this.SymbolMask = null
    }
    if (other.IsSwapEnabledSymbol != null) {
      this.IsSwapEnabledSymbol = other.IsSwapEnabledSymbol
    } else {
      this.IsSwapEnabledSymbol = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapImportSymbolSettings}
   * @returns {!SwapImportSymbolSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapImportSymbolSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapImportSymbolSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapImportSymbolSettings}
   * @param {!SwapImportSymbolSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapImportSymbolSettings)) {
      throw new TypeError('Instance of SwapImportSymbolSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapImportSymbolSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolMask: ((this.SymbolMask != null) ? this.SymbolMask : null),
      IsSwapEnabledSymbol: ((this.IsSwapEnabledSymbol != null) ? this.IsSwapEnabledSymbol : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapImportSymbolSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapImportSymbolSettings} other Object value
   * @returns {!SwapImportSymbolSettings} Created struct
   */
  static fromObject (other) {
    return new SwapImportSymbolSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapImportSymbolSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapImportSymbolSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapImportSymbolSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 186
  }
}

export { SwapImportSymbolSettings };

/**
 * Fast Binary Encoding SwapImportSymbolSettings field model
 */
class FieldModelSwapImportSymbolSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolMask = new fbe.FieldModelString(buffer, 4 + 4)
    this.IsSwapEnabledSymbol = new fbe.FieldModelBool(buffer, this.SymbolMask.fbeOffset + this.SymbolMask.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolMask.fbeSize + this.IsSwapEnabledSymbol.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolMask.fbeExtra + this.IsSwapEnabledSymbol.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapImportSymbolSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 186
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolMask.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolMask.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolMask.fbeSize

    if ((fbeCurrentSize + this.IsSwapEnabledSymbol.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsSwapEnabledSymbol.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSwapEnabledSymbol.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!SwapImportSymbolSettings} fbeValue Default value, defaults is new SwapImportSymbolSettings()
   * @returns {!SwapImportSymbolSettings} SwapImportSymbolSettings value
   */
  get (fbeValue = new SwapImportSymbolSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!SwapImportSymbolSettings} fbeValue SwapImportSymbolSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolMask.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolMask = this.SymbolMask.get()
    } else {
      fbeValue.SymbolMask = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolMask.fbeSize

    if ((fbeCurrentSize + this.IsSwapEnabledSymbol.fbeSize) <= fbeStructSize) {
      fbeValue.IsSwapEnabledSymbol = this.IsSwapEnabledSymbol.get()
    } else {
      fbeValue.IsSwapEnabledSymbol = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSwapEnabledSymbol.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapImportSymbolSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {!SwapImportSymbolSettings} fbeValue SwapImportSymbolSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapImportSymbolSettings}
   * @param {SwapImportSymbolSettings} fbeValue SwapImportSymbolSettings value
   */
  setFields (fbeValue) {
    this.SymbolMask.set(fbeValue.SymbolMask)
    this.IsSwapEnabledSymbol.set(fbeValue.IsSwapEnabledSymbol)
  }
}

export { FieldModelSwapImportSymbolSettings };

/**
 * Fast Binary Encoding SwapImportSymbolSettings model
 */
class SwapImportSymbolSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapImportSymbolSettings(this.buffer, 4)
  }

  /**
   * Get the SwapImportSymbolSettings model
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!FieldModelSwapImportSymbolSettings} model SwapImportSymbolSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapImportSymbolSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapImportSymbolSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapImportSymbolSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapImportSymbolSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapImportSymbolSettingsModel}
   * @param {!SwapImportSymbolSettings} value SwapImportSymbolSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapImportSymbolSettingsModel}
   * @param {!SwapImportSymbolSettings} value SwapImportSymbolSettings value, defaults is new SwapImportSymbolSettings()
   * @return {!object} Deserialized SwapImportSymbolSettings value and its size
   */
  deserialize (value = new SwapImportSymbolSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapImportSymbolSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapImportSymbolSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapImportSymbolSettingsModel}
   * @param {!number} prev Previous SwapImportSymbolSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapImportSymbolSettingsModel };
